/**
 * BatchAiEnrichPanel — Phase 4e.D++ (Feb 2026)
 *
 * Startet einen Batch-KI-Recherche-Lauf über mehrere Artikel und zeigt den
 * Fortschritt live (Polling alle 2s) im selben Sidebar-Pattern wie der
 * Inventur-Live-Feed: aktueller Artikel, Progress-Bar, ETA + History.
 */
import { useEffect, useRef, useState } from "react";
import { Sparkles, Loader2, Check, AlertTriangle, Play, X } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";

const POLL_MS = 2000;

export default function BatchAiEnrichPanel() {
    const [articles, setArticles] = useState([]);
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(new Set());
    const [activeJob, setActiveJob] = useState(null);
    const [loadingArticles, setLoadingArticles] = useState(false);
    const pollRef = useRef(null);

    const loadArticles = async () => {
        setLoadingArticles(true);
        try {
            const r = await api.get("/articles", { params: { search: search || undefined, limit: 100, in_stock: 1 } });
            setArticles(r.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Artikel-Suche fehlgeschlagen");
        }
        setLoadingArticles(false);
    };

    useEffect(() => { loadArticles(); /* eslint-disable-next-line */ }, []);

    const startBatch = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) { toast.error("Bitte mindestens einen Artikel auswählen"); return; }
        try {
            const r = await api.post("/articles/ai-enrich/batch/start", { article_ids: ids });
            toast.success(`Batch gestartet: ${ids.length} Artikel`);
            setActiveJob({ id: r.data.job_id, total: r.data.total, processed: 0, succeeded: 0, failed: 0, status: "running", items: [], current_article: null });
            setSelected(new Set());
        } catch (e) {
            toast.error(e.response?.data?.detail || "Batch-Start fehlgeschlagen");
        }
    };

    // Polling
    useEffect(() => {
        if (!activeJob?.id || activeJob.status !== "running") return undefined;
        pollRef.current = setInterval(async () => {
            try {
                const r = await api.get(`/articles/ai-enrich/batch/${activeJob.id}`);
                setActiveJob(r.data);
                if (r.data.status !== "running") {
                    clearInterval(pollRef.current);
                    if (r.data.status === "completed") {
                        toast.success(`Batch fertig: ${r.data.succeeded}/${r.data.total} erfolgreich`);
                    }
                }
            } catch (e) {
                clearInterval(pollRef.current);
            }
        }, POLL_MS);
        return () => clearInterval(pollRef.current);
    }, [activeJob?.id, activeJob?.status]);

    const toggleSelect = (id) => {
        setSelected((s) => {
            const next = new Set(s);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const selectAll = () => {
        if (selected.size === articles.length) setSelected(new Set());
        else setSelected(new Set(articles.slice(0, 50).map((a) => a.id)));
    };

    const progress = activeJob?.total > 0 ? (activeJob.processed / activeJob.total) * 100 : 0;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4" data-testid="batch-ai-panel">
            {/* MAIN */}
            <div className="space-y-3">
                <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs text-blue-900">
                    <strong>Batch-KI:</strong> Wählen Sie bis zu 50 Artikel aus. Die KI recherchiert pro Artikel im Netz (Beschreibung, Hersteller, EAN, technische Daten) und legt die Vorschläge ab. Sie können sie später pro Artikel einzeln übernehmen.
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") loadArticles(); }}
                        placeholder="Artikel suchen…"
                        className="flex-1 rounded-sm h-9"
                        data-testid="batch-ai-search"
                    />
                    <Button onClick={loadArticles} variant="outline" className="h-9 rounded-sm" data-testid="batch-ai-refresh">Suchen</Button>
                    <Button onClick={selectAll} variant="outline" className="h-9 rounded-sm" data-testid="batch-ai-select-all">
                        {selected.size === articles.length && articles.length > 0 ? "Alle abwählen" : `Top 50 wählen`}
                    </Button>
                    <Button
                        onClick={startBatch}
                        disabled={selected.size === 0 || activeJob?.status === "running"}
                        className="h-9 rounded-sm bg-[#111827] hover:bg-black text-white"
                        data-testid="batch-ai-start"
                    >
                        <Play className="w-4 h-4 mr-1" />
                        KI-Lauf starten ({selected.size})
                    </Button>
                </div>

                {loadingArticles && <div className="text-sm text-gray-500 py-2">Lädt…</div>}

                {!loadingArticles && (
                    <div className="border border-gray-200 rounded-sm overflow-hidden max-h-[500px] overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                                <tr className="text-left">
                                    <th className="px-3 py-2 w-10"></th>
                                    <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">SKU</th>
                                    <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">Name</th>
                                    <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">Hersteller</th>
                                    <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-500 text-right">Bestand</th>
                                </tr>
                            </thead>
                            <tbody>
                                {articles.slice(0, 100).map((a) => (
                                    <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => toggleSelect(a.id)} data-testid={`batch-ai-row-${a.id}`}>
                                        <td className="px-3 py-2">
                                            <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleSelect(a.id)} onClick={(e) => e.stopPropagation()} className="w-4 h-4 accent-[#111827]" />
                                        </td>
                                        <td className="px-3 py-2 font-mono-num text-xs">{a.sku}</td>
                                        <td className="px-3 py-2">
                                            {a.name}
                                            {a.has_pending_proposal && (
                                                <span
                                                    className="ml-2 inline-flex items-center gap-0.5 bg-[#FFCC00] text-[#111827] text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
                                                    title="KI-Vorschlag verfügbar — bitte in der Inbox prüfen"
                                                    data-testid={`batch-ai-proposal-badge-${a.id}`}
                                                >
                                                    ✨ KI
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-xs text-gray-600">{a.manufacturer || "—"}</td>
                                        <td className="px-3 py-2 text-right font-mono-num">{a.stock_qty || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* SIDEBAR — Live Progress */}
            <aside className="bg-gray-50 border border-gray-200 rounded-sm p-3 space-y-2 max-h-[600px] overflow-y-auto" data-testid="batch-ai-progress">
                <div className="flex items-center gap-2 sticky top-0 bg-gray-50 pb-2">
                    <Sparkles className="w-4 h-4 text-[#FFCC00]" />
                    <h3 className="font-bold text-sm">Aktiver Lauf</h3>
                </div>
                {!activeJob && (
                    <div className="text-xs text-gray-400 italic py-4 text-center">Kein aktiver Batch-Lauf.</div>
                )}
                {activeJob && (
                    <>
                        <div className="bg-white border border-gray-200 rounded-sm p-3 space-y-2" data-testid="batch-ai-active-job">
                            <div className="flex items-center justify-between text-xs">
                                <span className="font-bold">
                                    {activeJob.status === "running" ? "Läuft…" : activeJob.status === "completed" ? "Fertig ✓" : "Fehler"}
                                </span>
                                <span className="font-mono-num text-gray-500">{activeJob.processed}/{activeJob.total}</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-sm overflow-hidden">
                                <div className="h-full bg-[#FFCC00] transition-all duration-300" style={{ width: `${progress}%` }} />
                            </div>
                            {activeJob.current_article && (
                                <div className="text-[11px] text-gray-600">
                                    <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
                                    Artikel {activeJob.current_article.idx}/{activeJob.total}: {activeJob.current_article.name}
                                </div>
                            )}
                            {activeJob.eta_seconds > 0 && activeJob.status === "running" && (
                                <div className="text-[10px] text-gray-400">ETA: ~{activeJob.eta_seconds}s</div>
                            )}
                            <div className="flex items-center gap-3 text-[10px] pt-1 border-t border-gray-100">
                                <span className="text-emerald-700"><Check className="w-3 h-3 inline" /> {activeJob.succeeded || 0}</span>
                                <span className="text-red-600"><AlertTriangle className="w-3 h-3 inline" /> {activeJob.failed || 0}</span>
                            </div>
                        </div>
                        {(activeJob.items || []).slice().reverse().slice(0, 15).map((it, idx) => (
                            <div key={idx} className="bg-white border border-gray-200 rounded-sm p-2 text-[11px] space-y-0.5">
                                <div className="flex items-center gap-1.5">
                                    {it.status === "ok" ? <Check className="w-3 h-3 text-emerald-600" /> :
                                     it.status === "error" ? <X className="w-3 h-3 text-red-600" /> :
                                     <AlertTriangle className="w-3 h-3 text-amber-600" />}
                                    <span className="font-bold truncate">{it.name || it.article_id}</span>
                                </div>
                                {it.status === "ok" && <div className="text-gray-500 ml-4">{it.fields_count} Vorschläge bereit</div>}
                                {it.status === "error" && <div className="text-red-600 ml-4 line-clamp-2">{it.error}</div>}
                            </div>
                        ))}
                    </>
                )}
            </aside>
        </div>
    );
}
